import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Rocket, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { useSiteStatus } from '../../context/SiteStatusContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ComingSoonSettings = () => {
  const { refetchSiteStatus } = useSiteStatus();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [data, setData] = useState({
    coming_soon_enabled: false,
    headline: '',
    message: '',
  });

  const axiosCfg = { withCredentials: true };

  const fetchComingSoon = useCallback(async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${BACKEND_URL}/api/admin/settings/coming-soon`, axiosCfg);
      setData({
        coming_soon_enabled: Boolean(r.data.coming_soon_enabled),
        headline: r.data.headline || '',
        message: r.data.message || '',
      });
    } catch (e) {
      console.error('Failed to fetch coming soon settings', e);
      setMessage({ type: 'error', text: 'Failed to load coming soon settings.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchComingSoon();
  }, [fetchComingSoon]);

  const persist = async (next, successText) => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const r = await axios.put(
        `${BACKEND_URL}/api/admin/settings/coming-soon`,
        {
          coming_soon_enabled: next.coming_soon_enabled,
          coming_soon_headline: next.headline || null,
          coming_soon_message: next.message || null,
        },
        axiosCfg,
      );
      if (r.data?.success) {
        setData({
          coming_soon_enabled: Boolean(r.data.coming_soon_enabled),
          headline: r.data.headline || '',
          message: r.data.message || '',
        });
        refetchSiteStatus?.();
        setMessage({ type: 'success', text: successText });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      }
    } catch (e) {
      console.error('Failed to save coming soon settings', e);
      setMessage({
        type: 'error',
        text: e.response?.data?.detail || 'Failed to save coming soon settings.',
      });
      await fetchComingSoon();
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = () => {
    const next = { ...data, coming_soon_enabled: !data.coming_soon_enabled };
    setData(next);
    persist(
      next,
      next.coming_soon_enabled
        ? 'Coming soon mode enabled for the public site.'
        : 'Coming soon mode disabled — public site is live.',
    );
  };

  const handleSaveCopy = () => {
    persist(data, 'Coming soon page copy saved.');
  };

  if (loading) {
    return (
      <section
        data-testid="coming-soon-settings-card"
        className="bg-white rounded-xl border border-gray-200 p-6 mb-6 shadow-sm"
      >
        <div className="animate-pulse h-20 bg-gray-100 rounded-lg" />
      </section>
    );
  }

  const enabled = data.coming_soon_enabled;

  return (
    <section
      data-testid="coming-soon-settings-card"
      className="bg-white rounded-xl border border-gray-200 p-6 mb-6 shadow-sm"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-[#659A9B]/15 flex items-center justify-center flex-shrink-0">
            <Rocket className="w-5 h-5 text-[#659A9B]" />
          </div>
          <div className="min-w-0">
            <h2 className="text-xl font-bold text-gray-900">Coming Soon (public site)</h2>
            <p className="text-sm text-gray-600 mt-1">
              Hides flight/hotel search and booking for visitors. Admin, agent portal, and legal pages stay open.
              Saves instantly — no need to fill company information.
            </p>
            {enabled && (
              <span className="inline-flex mt-2 items-center gap-1 px-2 py-0.5 text-[11px] font-semibold bg-amber-100 text-amber-900 rounded">
                Public site is in coming soon mode
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          data-testid="coming-soon-toggle"
          onClick={handleToggle}
          disabled={saving}
          className={`relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#659A9B] disabled:opacity-50 ${
            enabled ? 'bg-[#659A9B]' : 'bg-gray-300'
          }`}
          aria-checked={enabled}
          role="switch"
          aria-label="Enable coming soon mode"
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
              enabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {message.text && (
        <div
          className={`mt-4 p-3 rounded-lg flex items-center gap-2 text-sm ${
            message.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
          )}
          {message.text}
        </div>
      )}

      {enabled && (
        <div className="mt-5 pt-5 border-t border-gray-200 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Headline (optional)
            </label>
            <input
              type="text"
              value={data.headline}
              onChange={(e) => setData((prev) => ({ ...prev, headline: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#659A9B] focus:border-transparent"
              placeholder="We're launching soon"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Message (optional)
            </label>
            <textarea
              rows={3}
              value={data.message}
              onChange={(e) => setData((prev) => ({ ...prev, message: e.target.value }))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#659A9B] focus:border-transparent"
              placeholder="ACF Tourism is getting ready..."
            />
          </div>
          <button
            type="button"
            onClick={handleSaveCopy}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-[#335E77] rounded-lg hover:opacity-90 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Save page copy
          </button>
        </div>
      )}
    </section>
  );
};

export default ComingSoonSettings;
